/*
Copyright (c) Karsten Jeske and John Firth
This source code is licensed under the proprietary license found in the
LICENSE file in the root directory of this source tree.
*/

import { useEffect, useState } from "react"
import {
  DATA_INFO_REQUEST,
  fetchErrorFingerprint,
  fetchErrorTags,
} from "services/fetching/requestContext"
import { Sentry } from "services/sentry"

interface ProjectionPeriod {
  startDate: string
  endDate: string
  returns: Record<string, number>
}

interface ProjectionPeriods {
  tenYear: ProjectionPeriod
  longTerm: ProjectionPeriod
}

interface DataInfo {
  earliestDataDate: string
  latestDataDate: string
  maxSimulationStartYear: number
  projectionPeriods: ProjectionPeriods
}

interface UseDataInfoReturn {
  dataInfo: DataInfo | null
  loading: boolean
  error: string | null
}

export const useDataInfo = (): UseDataInfoReturn => {
  const [dataInfo, setDataInfo] = useState<DataInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchDataInfo = async () => {
      try {
        setLoading(true)
        const response = await fetch(DATA_INFO_REQUEST.endpoint, {
          method: DATA_INFO_REQUEST.method,
        })
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
        const data = (await response.json()) as DataInfo
        setDataInfo(data)
        setError(null)
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Failed to fetch data info"
        setError(errorMessage)
        setDataInfo(null)

        // AIDEV-NOTE (#973): the fingerprint groups this endpoint's network failures together
        // regardless of how the browser worded the message or whether it supplied a stack, and
        // separately from the other endpoint's. Spread rather than passed as a key so a non-fetch
        // failure here - a non-ok status, a body that will not parse - keeps Sentry's own
        // grouping instead of being merged in with the network ones.
        const fingerprint = fetchErrorFingerprint(err, DATA_INFO_REQUEST)
        Sentry.captureException(err instanceof Error ? err : new Error(errorMessage), {
          tags: {
            errorType: "data_fetch_error",
            hook: "useDataInfo",
            ...fetchErrorTags(DATA_INFO_REQUEST),
          },
          ...(fingerprint ? { fingerprint } : {}),
        })
      } finally {
        setLoading(false)
      }
    }

    void fetchDataInfo()
  }, [])

  return { dataInfo, loading, error }
}
